import { AggregatedValues } from '@approot/models/summed-legs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import {
  LegService,
  ProductsConfigService,
  UserStorageService
} from '@approot/services';
import { Subscription } from 'rxjs';
import {
  QuotePair,
  UserPreferencesState,
  UserPreferencesStateKey
} from '@approot/models';

type Rows =
  | 'premiumQuoteCurrencyInPercent'
  | 'premiumQuoteCurrencyPips'
  | 'premiumBaseCurrency'
  | 'premiumBaseCurrencyInPercent'
  | 'upfrontPremiumBsMid'
  | 'spotDelta'
  | 'spotDeltaPercent'
  | 'forwardDelta'
  | 'forwardDeltaPercent'
  | 'brokerDelta'
  | 'brokerDeltaPercent'
  | 'gamma'
  | 'vega'
  | 'riskies'
  | 'flies';

type Result =
  | 'dcdSpecifications'
  | 'markUpSpecifications'
  | 'marketDataSpecifications'
  | 'resultSpecifications';
@Component({
  selector: 'app-sum-results-display',
  templateUrl: './sum-results-display.component.html',
  styleUrls: ['./sum-results-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SumResultsDisplayComponent implements OnDestroy, OnChanges {
  @Input() public aggregatedValues: AggregatedValues;
  @Input() public cross: QuotePair;
  public rowsToShow = {
    premiumQuoteCurrencyInPercent: false,
    premiumQuoteCurrencyPips: false,
    premiumBaseCurrency: false,
    premiumBaseCurrencyInPercent: false,
    upfrontPremiumBsMid: false,
    spotDelta: false,
    spotDeltaPercent: false,
    forwardDelta: false,
    forwardDeltaPercent: false,
    brokerDelta: false,
    brokerDeltaPercent: false,
    gamma: false,
    vega: false,
    riskies: false,
    flies: false
  };
  private pricingDisplaySettings: { [key: string]: boolean } = {};
  private sub: Subscription;

  constructor(
    private legService: LegService,
    private userStorageService: UserStorageService,
    private changeRef: ChangeDetectorRef,
    private productsConfigService: ProductsConfigService
  ) {
    this.sub = this.userStorageService
      .storeChangesObservable<UserPreferencesState>(UserPreferencesStateKey)
      .subscribe(prefs => {
        this.pricingDisplaySettings = prefs.PricingDisplayPrefs;
        this.updateAllIsRowEnabled();
        this.changeRef.markForCheck();
      });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.aggregatedValues?.currentValue) {
      this.updateAllIsRowEnabled();
    }
  }

  public ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateAllIsRowEnabled(): void {
    Object.keys(this.rowsToShow).forEach(field => {
      this.rowsToShow[field] = this.isRowEnabled(
        field as Rows,
        'resultSpecifications'
      );
    });
  }

  private isRowEnabled(name: Rows, result: Result): boolean {
    const resultAvailable = !!this.productsConfigService.pricingResultsInfo[
      result
    ].find(result => result.shortName === name);

    const ret = this.pricingDisplaySettings[name];
    return (ret ?? true) && resultAvailable;
  }

  public get pricingResultsExpanded() {
    return this.legService.pricingResultsExpanded;
  }
}
